import React, {ComponentType, useContext} from 'react'
import {Navigate} from "react-router";
import {AppContext} from "../context/app-context";
import {getLandingRedirectPathForUser} from "./route-utils";
import _ from 'underscore'
import AccountUtilService from "../services/account/AccountUtilService";
import {isSupplierAccount} from "../util/AccountUtils";
import { Account } from "../types/Account";

interface AuthorizationConfig {
  userTypes: Array<string>;
  accountTypes: Array<string>;
  subscriptionTypes: Array<string>;
  additionalValidation?: (user: any) => boolean;
}

/**
 * Extra props that route-level callers may pass to a protected component.
 * `account` is intentionally absent: it is always injected from `AppContext`
 * by `withAccessControl` after authorization succeeds, so callers must never
 * supply it themselves.
 */
type AccessControlAdditionalProps = {
  handleLogout?: () => void
}

/**
 * Higher-order component that wraps a given component and enforces authorization
 * before rendering it.
 *
 * @param WrappedComponent The component to protect. If account is provided as a prop,
 * it will be stripped and injected from context after authorization checks.
 * @param authorized One or more authorization configurations (user types,
 *   account types, subscription types, optional custom validation).
 * @returns A component that injects `account` from context, forwards optional `handleLogout` from its caller, and performs authorization checks before rendering or redirecting if unauthorized.
 *
 * This mirrors the `withSnackbar` pattern, which strips `snackbarShowMessage`
 * from the external interface and injects it from context.
 *
 * @example
 * const ProtectedMyPage = withAccessControl(MyPage, {
 *   userTypes: ['admin', 'owner'],
 *   accountTypes: ['supplier'],
 *   subscriptionTypes: ['premium'],
 *   additionalValidation: (user) => user.isActive,
 * });
 *
 * // Callers only pass props other than `account` — it is injected automatically:
 * <ProtectedMyPage handleLogout={logout} />
 *
 */
export const withAccessControl = <P extends object>(
  WrappedComponent: ComponentType<P>,
  ...authorized: AuthorizationConfig[]
) => {
  const ProtectedComponent = (props: Omit<P, 'account'> & AccessControlAdditionalProps) => {
    const appContext = useContext(AppContext);
    const {loadingUserDetails, user} = appContext;
    const {userType, type, subscriptionType} = user || {};

    let hasAuthorization = false;

    // Authorization Check Phase:
    // Validate user against all provided authorization configurations
    if (authorized.length > 0) {
      for (let i = 0; i < authorized.length; i++) {
        const {
          userTypes: allowedUserTypes,
          accountTypes: allowedAccountTypes,
          subscriptionTypes: allowedSubscriptionTypes,
          additionalValidation
        } = authorized[i];

        // Primary authorization check: userType and accountType must match
        // null or undefined userType or type will result in false value
        hasAuthorization = _.contains(allowedUserTypes, userType)
          && _.contains(allowedAccountTypes, type);

        // Additional check for supplier accounts: validate subscription type
        if (isSupplierAccount(user)) {
          hasAuthorization = hasAuthorization && _.contains(allowedSubscriptionTypes, subscriptionType);
        }

        // Custom validation logic if provided
        if (additionalValidation) {
          hasAuthorization = hasAuthorization && additionalValidation(user);
        }

        // Terms of service confirmation check
        if (AccountUtilService.userNeedsTosConfirmation(user)) {
          hasAuthorization = false;
        }

        // Short-circuit: if any authorization config passes, user is authorized
        if (hasAuthorization) {
          break;
        }
      }
    }

    // Redirect Phase:
    // If authorization fails, redirect user to appropriate landing page
    if (!hasAuthorization) {
      return loadingUserDetails ?
        <div/>
        :
        <Navigate to={String(getLandingRedirectPathForUser(user))}/>;
    }

    // Render Phase with Type Safety Guarantee:
    // At this point, authorization has passed, which guarantees:
    // 1. user is not null/undefined (authorization checks would have failed)
    // 2. user has valid userType and type properties
    // 3. user passed all additional validation checks
    // 4. We can safely cast user as Account for the wrapped component

    // Reconstruct the full P by injecting account from context.
    // `props` is typed as Omit<P, 'account'>, so we spread it and add the
    // now-guaranteed non-null account. The double cast is necessary because
    // TypeScript cannot verify the Omit+account reassembly statically.
    const authorizedProps = {
      ...props,
      account: user as Account
    } as unknown as P;

    return <WrappedComponent {...authorizedProps} />;
  };

  ProtectedComponent.displayName = `withAccessControl(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return ProtectedComponent;
};

